/**
 * Mux delivery URLs — the ONE place any app builds a Mux locator.
 *
 * A Mux-backed `Media` row is not addressable the way every other media row is.
 * It has NO `cloudinaryUrl`, and its `s3Key` is a PLACEHOLDER minted at
 * upload-URL creation (`mux/<schoolPublicId>/<mediaPublicId>.<ext>`) purely so
 * the NOT NULL column has a value and the row is identifiable in raw queries.
 * The bytes go to Mux — **no S3 object is ever written**, and `mux/` sits
 * outside the media bucket's public-read prefix. An object URL rebuilt from that
 * key therefore cannot 404; it answers `403 AccessDenied` (a nonexistent key
 * without `s3:ListBucket` is indistinguishable from a forbidden one). That is
 * the whole of the "attached videos don't play" defect: the feed handed clients
 * a URL that could only ever 403, and the player retried it.
 *
 * The row's real locator is `muxPlaybackId`, webhook-populated on
 * `video.asset.ready`. Until it lands there is no playable URL at all, only a
 * media id to poll — hence the `mux-pending://` sentinel below.
 *
 * These builders lived in three places before this module (mobile's
 * `components/media/mux-video-player.tsx` and `domains/media/hooks/use-mux-upload.ts`,
 * plus inline template strings in two web media-library components) and the
 * backend had none at all, which is exactly why the feed read path invented its
 * own broken answer. One owner, imported by all three apps.
 */

/** Mux HLS delivery host. */
export const MUX_STREAM_HOST = 'https://stream.mux.com'

/** Mux image (poster/thumbnail) delivery host. */
export const MUX_IMAGE_HOST = 'https://image.mux.com'

/**
 * Mux's hosted, iframe-embeddable player page. Distinct from the HLS host: this
 * is what an author pastes into docs MDX, because neither the manifest nor the
 * poster embeds in an `<iframe>`.
 */
export const MUX_PLAYER_HOST = 'https://player.mux.com'

/**
 * The `s3Key` prefix the Mux upload path writes as a placeholder. Nothing is
 * ever stored under it — see the module note.
 */
export const MUX_PLACEHOLDER_S3_PREFIX = 'mux/'

/**
 * Sentinel URL for a Mux video whose encoding webhook has not landed yet: there
 * is no playable URL, only a media id to poll. Deliberately NOT an `http(s)`
 * scheme, so no client can turn it into a doomed request — readers recognise
 * the prefix and resolve the playback id from the underlying `Media` row.
 *
 * Originated in the chat upload channel; the feed read path now emits it for
 * the same state, so one client branch covers both surfaces.
 */
export const MUX_PENDING_URL_PREFIX = 'mux-pending://'

/** HLS manifest for a ready Mux asset. */
export function buildMuxHlsUrl(playbackId: string): string {
  return `${MUX_STREAM_HOST}/${playbackId}.m3u8`
}

/**
 * Poster/thumbnail frame for a ready Mux asset. `width` is optional — Mux
 * serves a default-sized frame without it.
 */
export function buildMuxPosterUrl(playbackId: string, options?: { width?: number }): string {
  const base = `${MUX_IMAGE_HOST}/${playbackId}/thumbnail.jpg`
  return options?.width ? `${base}?width=${options.width}` : base
}

/** Embeddable player page for a ready Mux asset. */
export function buildMuxPlayerUrl(playbackId: string): string {
  return `${MUX_PLAYER_HOST}/${playbackId}`
}

/** The pending sentinel for a media row that has no playback id yet. */
export function buildMuxPendingUrl(mediaId: string): string {
  return `${MUX_PENDING_URL_PREFIX}${mediaId}`
}

/**
 * Read the media id back out of a pending sentinel, or `null` when the input is
 * not one.
 *
 * NULL-SAFE BY CONTRACT. The mobile copy this replaces took `url: string` and
 * called `.startsWith` on it unguarded, so a `null`/absent attachment url —
 * exactly what a Mux row with no playback id produces once the feed stops
 * synthesizing a dead S3 URL — threw a TypeError inside the video tile's render
 * and took the whole feed screen down. Every caller passes a value that can be
 * absent; the guard belongs here, once.
 */
export function parseMuxPendingUrl(url: string | null | undefined): { mediaId: string } | null {
  if (typeof url !== 'string' || !url.startsWith(MUX_PENDING_URL_PREFIX)) return null
  const mediaId = url.slice(MUX_PENDING_URL_PREFIX.length)
  return mediaId ? { mediaId } : null
}

/**
 * Is this `s3Key` the Mux placeholder rather than a real object key? A `true`
 * answer means "there is no S3 object here" — never build a URL from it.
 */
export function isMuxPlaceholderS3Key(s3Key: string | null | undefined): boolean {
  return typeof s3Key === 'string' && s3Key.startsWith(MUX_PLACEHOLDER_S3_PREFIX)
}

/** Pull the playback id back out of a Mux HLS URL, or `null` if it is not one. */
export function parseMuxHlsUrl(url: string | null | undefined): string | null {
  if (typeof url !== 'string') return null
  const match = url.match(/^https:\/\/stream\.mux\.com\/([^./?#]+)\.m3u8/i)
  return match ? match[1] : null
}
